import { DataType, Form, FormOptions, FormSkeleton, Section } from '@globaleris/ui.library'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddressTypeEnum } from '../../../api/Argentia/enums/addressTypeEnum'
import { SearchSelectOption } from '../../../model/searchSelectOption'
import { getCustomerDomain } from '../../../redux'
import { fetchPostalCodes, getPostalAddressByID } from '../../clients/client.service'
import { AddressDataFormModel } from '../addresses.model'

export interface AddressProps {
  address?: AddressDataFormModel
  addressType: AddressTypeEnum
  onDataChange: (dataForm: AddressDataFormModel) => void
  onError: (errors: boolean) => void
}

export const AddressForm: React.FC<AddressProps> = ({
  address,
  addressType,
  onDataChange,
  onError
}) => {
  const [t] = useTranslation(['addresses', 'enums'])
  const [errors, setErrors] = useState<boolean>()

  //Form config Starts
  const options: FormOptions = useMemo(() => {
    return {
      elevation: 0,
      columns: 3,
      analytics: {
        customer: getCustomerDomain(),
        view: t('addresses:forms.analytics')
      }
    }
  }, [getCustomerDomain])

  const updateAddress = async (option: SearchSelectOption | null) => {
    if (!option) {
      // So Save Button is disabled
      onError(true)

      onDataChange({
        ...address,
        municipalityId: undefined,
        municipality: '',
        provinceId: undefined,
        province: '',
        countryId: undefined,
        country: '',
        postalCode: ''
      })
      return
    }

    const postalAddress = await getPostalAddressByID(+option.value).catch(e => {
      console.log(e)
    })

    if (postalAddress) {
      onError(false)
      onDataChange({
        ...address,
        municipalityId: postalAddress.municipality.id,
        municipality: postalAddress.municipality.description,
        provinceId: postalAddress.province.id,
        province: postalAddress.province.description,
        countryId: postalAddress.country.id,
        country: postalAddress.country.description,
        postalCode: postalAddress.postalCode,
        postalCodeSearch: {
          value: option.value,
          label: `${option.value}`,
          textValue: `${option.value}`
        }
      })
    }
  }

  const sectionsForm: Section[] = [
    {
      title:
        addressType === AddressTypeEnum.Billing
          ? t('addresses:titles.billing')
          : t('addresses:titles.supply'),
      inputs: [
        {
          name: 'postalAddress',
          label: t('addresses:forms.inputs.postalAddress'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'postalCodeSearch',
          label: t('addresses:forms.inputs.postalCode'),
          type: DataType.SEARCHFIELD,
          apiCall: fetchPostalCodes,
          defaultValue: { value: address?.postalCode, label: address?.postalCode },
          maxLength: 5,
          required: true,
          clearable: true,
          onChange: newCode => {
            const option = newCode as any
            updateAddress(option as SearchSelectOption | null)
          }
        },
        {
          name: 'municipalityId',
          label: t('addresses:forms.inputs.municipality'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'provinceId',
          label: t('addresses:forms.inputs.province'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'countryId',
          label: t('addresses:forms.inputs.country'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        }
      ]
    }
  ]
  //Form config Ends
  return address ? (
    <Form title="" sections={sectionsForm} data={address} options={options} />
  ) : (
    <FormSkeleton columns={3} inputs={5} />
  )
}
